import {
  AdditiveBlending,
  BoxGeometry,
  ClampToEdgeWrapping,
  Color,
  GLSL3,
  LinearFilter,
  Matrix4,
  Mesh,
  RawShaderMaterial,
  TextureLoader,
  Vector3
} from "three";
import { createGuiUpdater, v4 } from "@/utils";
import type { Step } from "../types";
import { stripUnusedAttrs } from "../utils";
import frag from "./shaders/fragment.frag";
import vertex from "./shaders/vertex.vert";
import type { Flame, FlameHandlers, FlameInterface } from "./types";

/**
 * Creates a volumetric flame mesh using animated raymarching shaders.
 *
 * @param texture          - Optional texture for the flame appearance.
 * @param props.startPozs  - Flame position in world space.
 * @param props.size       - Base size of the flame geometry.
 * @param props.speed      - Controls how fast the flame animates.
 * @param props.color      - Base color applied in the shader.
 * @param props.seed       - Random seed for animation offset.
 * @param props.noise      - Noise config for procedural flame shape.
 * @param props.march      - Raymarching config (iterations & step factor).
 * @param props.colorMixStr- How much the final color mixes with base color.
 *
 * @returns { step, updtScreen }
 *   flame            - Mesh
 *   step(delta)      - Call each frame to spawn & advance particles.
 *   update(params)   - Update particle system parameters in real-time.
 */
export const createFlame = ({
  texture,
  startPozs,
  size,
  uTimeMult = 1,
  color,
  seed = Math.random() * 19.19,
  noise,
  march,
  colorMixStr
}: FlameInterface): Flame => {
  const loader = new TextureLoader();
  const fireTex = loader.load(texture);
  if (fireTex) {
    fireTex.flipY = false;
    fireTex.wrapS = fireTex.wrapT = ClampToEdgeWrapping;
    fireTex.magFilter = fireTex.minFilter = LinearFilter;
  }
  const geometry = new BoxGeometry(size, size * 1.45, size);

  const invModelMatrix = new Matrix4();

  const u_radius = size * 0.5;
  const u_height = size;
  const u_bottom = -size * 0.8;

  const uniforms = {
    diffText: { value: fireTex },
    u_time: { value: 0 },
    seed: { value: seed },
    invModelMatrix: { value: invModelMatrix },
    scale: { value: new Vector3(size, size, size) },

    // noise
    noiseScale: { value: v4(noise.noiseScale) },
    magnitude: { value: noise.magnitude },
    lacunarity: { value: noise.lacunarity },
    gain: { value: noise.gain },
    u_octaves: { value: noise.octaves },
    u_iterations: { value: march.iterations },

    // shape
    u_radius: { value: u_radius },
    u_height: { value: u_height },
    u_bottom: { value: u_bottom },

    color: {
      value: new Color(color)
    },
    colorMixStrength: { value: colorMixStr }
  };

  const material = new RawShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: vertex,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    uniforms,
    blending: AdditiveBlending,
    toneMapped: false
  });

  const flame = new Mesh(geometry, material);
  flame.position.set(startPozs.x, startPozs.y, startPozs.z);

  flame.renderOrder = 3;
  flame.updateMatrixWorld(true);
  uniforms.invModelMatrix.value.copy(flame.matrixWorld).invert();

  //Animation steps
  const step: Step = (delta) => {
    uniforms.u_time.value += delta * uTimeMult;
  };

  //
  // ------------------ GUI Updater ------------------
  //
  const guiHandlers: FlameHandlers = {
    size: (v) => {
      uniforms.scale.value.setScalar(v);
      uniforms.u_radius.value = v * 0.6;
      uniforms.u_height.value = v;
      uniforms.u_bottom.value = -v * 0.8;
    },

    uTimeMult: (v) => {
      uTimeMult = v || 1;
    },

    color: (v) => {
      uniforms.color.value.set(new Color(v));
    },

    colorMixStr: (v) => {
      uniforms.colorMixStrength.value = v;
    },

    startPozs: (v) => {
      flame.position.set(v.x, v.y, v.z);
      flame.updateMatrixWorld();
      uniforms.invModelMatrix.value.copy(flame.matrixWorld).invert();
    },

    "noise.magnitude": (v) => {
      uniforms.magnitude.value = v;
    },

    "noise.noiseScale": (v) => {
      uniforms.noiseScale.value.set(v.x, v.y, v.z, v.w);
    },

    "noise.lacunarity": (v) => {
      uniforms.lacunarity.value = v;
    },

    "noise.gain": (v) => {
      uniforms.gain.value = v;
    },

    "noise.octaves": (v) => {
      uniforms.u_octaves.value = v;
    }
  };

  const update = createGuiUpdater(guiHandlers);

  stripUnusedAttrs(flame.geometry);

  return { flame, step, update };
};
